/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { ActionContext, ActionTree, GetterTree, MutationTree, StoreOptions } from 'vuex';
import createLogger from 'vuex/dist/logger';
import { uniqueId } from 'lodash';

import { RootState } from '@/state/types';

import { Alert } from './types';

const debug = process.env.NODE_ENV !== 'production';
const ALERT_DISPLAY_TIME = 7000;

interface State {
  list: Alert[];
}

const state: State = {
  list: [],
};

const mutations: MutationTree<State> = {
  ADD_ALERT(state: State, alert: Alert) {
    state.list.unshift(alert);
  },
  REMOVE_ALERT(state: State, alert: Alert) {
    const idx = state.list.indexOf(alert);
    if (idx !== -1) {
      state.list.splice(state.list.indexOf(alert), 1);
    }
  },
};

const actions: ActionTree<State, RootState> = {
  show({ dispatch }: ActionContext<State, RootState>, alerts: Alert | Alert[]) {
    alerts = Array.isArray(alerts) ? alerts : [alerts];
    alerts.forEach((alert: Alert) => dispatch('showOne', alert));
  },
  showOne({ commit, dispatch }: ActionContext<State, RootState>, alert: Alert) {
    if (!alert.type || !alert.message) {
      dispatch('show', {
        type: 'error',
        message: 'Oops, something went wrong.',
      });
      return;
    }

    alert.id = uniqueId();
    commit('ADD_ALERT', alert);

    if (!alert.stay) {
      alert.timer = window.setTimeout(() => {
        dispatch('hideOne', alert);
      }, ALERT_DISPLAY_TIME);
    }
  },
  hideOne({ commit }: ActionContext<State, RootState>, alert: Alert) {
    window.clearTimeout(alert.timer);
    commit('REMOVE_ALERT', alert);
  },
};

const getters: GetterTree<State, RootState> = {
  list(state: State) {
    return state.list;
  },
};

export default (): StoreOptions<State> => ({
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  strict: debug,
  plugins: debug ? [createLogger({})] : [],
});
